// Turns a model's run result into what a reviewer needs to decide on a prompt change:
// an honest verdict (stats.ts), per-stage baseline → candidate scores, and, per field and
// per item, what the candidate FIXED (baseline wrong, candidate right) and what it BROKE
// (baseline right, candidate wrong). A single delta hides these; +0 can be one fix and one
// break. Pure, so the views stay presentational.

import { comparePaired, type PairedComparison } from './stats'
import type { FieldResult, Stage } from './scoring'
import type { ItemResult, ModelResult } from './types'

export type FieldChange = {
  stage: Stage
  field: string
  /** 1-based person index for per-person fields; aligned to the golden's people. */
  person?: number
  kind: 'fixed' | 'broke'
  goldenValue: string
  baselineValue: string
  candidateValue: string
}

export type ItemStatus = 'regressed' | 'improved' | 'tied'

export type ItemAnalysis = {
  result: ItemResult
  status: ItemStatus
  /** Candidate minus baseline item accuracy. */
  delta: number
  changes: FieldChange[]
}

export type Tally = { matched: number; total: number }

export type StageRow = { stage: Stage; baseline: Tally; candidate: Tally }

export type FieldMovement = {
  stage: Stage
  field: string
  baseline: Tally
  candidate: Tally
  fixed: number
  broke: number
}

export type ModelAnalysis = {
  model: string
  comparison: PairedComparison
  /** Fields scored per prompt across the run: the resolution of every percentage. */
  totalFields: number
  baseline: Tally
  candidate: Tally
  stages: StageRow[]
  /** Every scored field, most-moved first. */
  fields: FieldMovement[]
  /** Regressions first (worst first), then improvements (best first), then ties. */
  items: ItemAnalysis[]
}

const STATUS_ORDER: Record<ItemStatus, number> = { regressed: 0, improved: 1, tied: 2 }
const fieldKey = (field: FieldResult) => `${field.field}#${field.person ?? 0}`

export type FieldSide = { value: string; matched: boolean }

/** One field of one item under both prompts, for a side-by-side comparison. */
export type FieldRow = {
  stage: Stage
  field: string
  /** 1-based person index for per-person fields; aligned to the golden's people. */
  person?: number
  goldenValue: string
  /** Undefined when this prompt did not produce the person at all. */
  baseline?: FieldSide
  candidate?: FieldSide
  change?: 'fixed' | 'broke'
}

/**
 * Every scored field of one item under both prompts. A field present on one side only is
 * a person one prompt produced and the other did not (an extra or a miss); on the missing
 * side it counts as not matched, so producing a wrong extra person "broke" its fields.
 */
export function itemFieldRows(item: ItemResult, stages: Stage[]): FieldRow[] {
  return stages.flatMap((stage) => {
    const baseline = new Map(item.baselineScore.stages[stage].fields.map((field) => [fieldKey(field), field]))
    const candidate = new Map(item.candidateScore.stages[stage].fields.map((field) => [fieldKey(field), field]))
    return [...new Set([...baseline.keys(), ...candidate.keys()])].map((key): FieldRow => {
      const before = baseline.get(key)
      const after = candidate.get(key)
      const beforeMatched = before?.matched ?? false
      const afterMatched = after?.matched ?? false
      const reference = after ?? before!
      return {
        stage,
        field: reference.field,
        person: reference.person,
        goldenValue: reference.goldenValue,
        baseline: before && { value: before.candidateValue, matched: before.matched },
        candidate: after && { value: after.candidateValue, matched: after.matched },
        change: beforeMatched === afterMatched ? undefined : afterMatched ? `fixed` : `broke`,
      }
    })
  })
}

function itemChanges(item: ItemResult, stages: Stage[]): FieldChange[] {
  return itemFieldRows(item, stages).flatMap((row): FieldChange[] =>
    row.change
      ? [
          {
            stage: row.stage,
            field: row.field,
            person: row.person,
            kind: row.change,
            goldenValue: row.goldenValue,
            baselineValue: row.baseline?.value ?? `(missing)`,
            candidateValue: row.candidate?.value ?? `(missing)`,
          },
        ]
      : [],
  )
}

function tally(scores: { matched: number; total: number }[]): Tally {
  return scores.reduce((sum, score) => ({ matched: sum.matched + score.matched, total: sum.total + score.total }), {
    matched: 0,
    total: 0,
  })
}

/** `margin`: the smallest difference worth acting on (see stats.comparePaired). */
export function analyzeModel(result: ModelResult, stages: Stage[], seed: string, margin?: number): ModelAnalysis {
  const items: ItemAnalysis[] = result.items
    .map((item) => ({
      result: item,
      status: (item.regressed ? `regressed` : item.improved ? `improved` : `tied`) as ItemStatus,
      delta: item.candidateScore.overall - item.baselineScore.overall,
      changes: itemChanges(item, stages),
    }))
    .sort((a, b) =>
      STATUS_ORDER[a.status] !== STATUS_ORDER[b.status]
        ? STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
        : a.status === `regressed`
          ? a.delta - b.delta
          : b.delta - a.delta,
    )

  const stageRows: StageRow[] = stages.map((stage) => ({
    stage,
    baseline: tally(result.items.map((item) => item.baselineScore.stages[stage])),
    candidate: tally(result.items.map((item) => item.candidateScore.stages[stage])),
  }))

  const movement = new Map<string, FieldMovement>()
  for (const item of result.items) {
    for (const stage of stages) {
      for (const [side, score] of [
        [`baseline`, item.baselineScore],
        [`candidate`, item.candidateScore],
      ] as const) {
        for (const field of score.stages[stage].fields) {
          const key = `${stage}.${field.field}`
          const entry = movement.get(key) ?? {
            stage,
            field: field.field,
            baseline: { matched: 0, total: 0 },
            candidate: { matched: 0, total: 0 },
            fixed: 0,
            broke: 0,
          }
          entry[side].matched += field.matched ? 1 : 0
          entry[side].total += 1
          movement.set(key, entry)
        }
      }
    }
  }
  for (const { changes } of items) {
    for (const change of changes) {
      const entry = movement.get(`${change.stage}.${change.field}`)
      if (entry) entry[change.kind] += 1
    }
  }

  const baseline = tally(result.items.map((item) => item.baselineScore))
  const candidate = tally(result.items.map((item) => item.candidateScore))
  return {
    model: result.model,
    comparison: comparePaired(
      result.items.map((item) => ({ candidate: item.candidateScore, baseline: item.baselineScore })),
      `${seed}:${result.model}`,
      margin,
    ),
    totalFields: candidate.total,
    baseline,
    candidate,
    stages: stageRows,
    fields: [...movement.values()].sort(
      (a, b) =>
        b.fixed + b.broke - (a.fixed + a.broke) ||
        a.candidate.matched / (a.candidate.total || 1) - b.candidate.matched / (b.candidate.total || 1),
    ),
    items,
  }
}
